import { useRef, useState } from "react";
import { useWidth } from "./useWidth";
import { compact, niceTicks, roundedRightPath, roundedTopPath, seriesColor } from "./util";

export interface BarItem {
  label: string;
  value: number;
  /** Optional explicit color (e.g. a status color). Defaults to categorical slot 1 for every bar: a single measure is one color. */
  color?: string;
  /** Secondary text shown in the tooltip. */
  hint?: string;
}

export interface BarChartProps {
  items: BarItem[];
  orientation?: "horizontal" | "vertical";
  ariaLabel: string;
  formatValue?: (v: number) => string;
  height?: number;
  /** Label every bar's value directly (only when the count is small). Default: label when ≤ 8 bars. */
  directLabels?: boolean;
  /** Fixed max for the value axis. */
  max?: number;
}

const THICK = 20; // ≤ 24px
const GAP = 2; // surface gap
const LABEL_W = 150;

/**
 * Bars ≤24px thick with a 4px rounded data-end and a square baseline, 2px
 * surface gap between neighbors, values at the tip when they fit, per-bar
 * hover tooltip (the mark is the hit target), and a table fallback via
 * <title>. One measure, one color; use `color` only for status encoding.
 */
export function BarChart({ items, orientation = "horizontal", ariaLabel, formatValue = compact, height, directLabels, max }: BarChartProps) {
  const [hover, setHover] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const measured = useWidth(ref, 640);
  const top = niceTicks(max ?? Math.max(0, ...items.map((i) => i.value)), 4).slice(-1)[0] || 1;
  const labelAll = directLabels ?? items.length <= 8;

  if (items.length === 0) {
    return (
      <div className="viz viz-chart" ref={ref}>
        <div className="viz-empty" role="img" aria-label={`${ariaLabel}: no data`}>
          No data
        </div>
      </div>
    );
  }

  if (orientation === "horizontal") {
    const width = measured;
    const labelW = Math.min(LABEL_W, Math.round(width * 0.3));
    const rowH = THICK + GAP + 8;
    const h = items.length * rowH + 8;
    const plotW = width - labelW - 60;
    return (
      <div className="viz viz-chart" ref={ref}>
        <svg viewBox={`0 0 ${width} ${h}`} width={width} height={h} role="img" aria-label={ariaLabel}>
          <title>{ariaLabel}</title>
          {items.map((it, i) => {
            const y = 4 + i * rowH;
            const w = Math.max(0, (it.value / top) * plotW);
            const color = it.color ?? seriesColor(0);
            const fits = w > 44;
            return (
              <g key={it.label} onPointerEnter={() => setHover(i)} onPointerLeave={() => setHover(null)}>
                <rect className="viz-bar-hit" x={0} y={y - GAP} width={width} height={rowH} />
                <text className="viz-label" x={labelW - 8} y={y + THICK / 2 + 4} textAnchor="end">
                  {it.label.length > 24 ? `${it.label.slice(0, 23)}…` : it.label}
                </text>
                <path className={`viz-bar ${hover === i ? "hover" : ""}`} fill={color} d={roundedRightPath(labelW, y, w, THICK)}>
                  <title>{`${it.label}: ${formatValue(it.value)}${it.hint ? ` · ${it.hint}` : ""}`}</title>
                </path>
                {(labelAll || hover === i) && (
                  <text className="viz-label strong" x={labelW + w + (fits ? -6 : 6)} y={y + THICK / 2 + 4} textAnchor={fits ? "end" : "start"} fill={fits ? "#fff" : undefined} style={fits ? { fill: "#fff" } : undefined}>
                    {formatValue(it.value)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
    );
  }

  // vertical columns
  const width = measured;
  const H = height ?? 180;
  const padL = 40;
  const padB = 22;
  const plotH = H - padB - 10;
  const slot = (width - padL - 8) / items.length;
  const bw = Math.min(THICK + 4, slot - GAP * 2);
  const ticks = niceTicks(max ?? Math.max(0, ...items.map((i) => i.value)), 4);
  const yAt = (v: number) => 10 + plotH - (v / top) * plotH;
  return (
    <div className="viz viz-chart" ref={ref}>
      <svg viewBox={`0 0 ${width} ${H}`} width={width} height={H} role="img" aria-label={ariaLabel}>
        <title>{ariaLabel}</title>
        {ticks.map((tk) => (
          <g key={tk}>
            <line className={tk === 0 ? "viz-axis" : "viz-grid"} x1={padL} x2={width - 8} y1={yAt(tk)} y2={yAt(tk)} />
            <text className="viz-tick" x={padL - 6} y={yAt(tk) + 3} textAnchor="end">
              {formatValue(tk)}
            </text>
          </g>
        ))}
        {items.map((it, i) => {
          const x = padL + i * slot + (slot - bw) / 2;
          const y = yAt(it.value);
          const hgt = 10 + plotH - y;
          const color = it.color ?? seriesColor(0);
          return (
            <g key={it.label} onPointerEnter={() => setHover(i)} onPointerLeave={() => setHover(null)}>
              <rect className="viz-bar-hit" x={padL + i * slot} y={0} width={slot} height={H} />
              <path className={`viz-bar ${hover === i ? "hover" : ""}`} fill={color} d={roundedTopPath(x, y, bw, hgt)}>
                <title>{`${it.label}: ${formatValue(it.value)}${it.hint ? ` · ${it.hint}` : ""}`}</title>
              </path>
              {(labelAll || hover === i) && (
                <text className="viz-label strong" x={x + bw / 2} y={y - 5} textAnchor="middle">
                  {formatValue(it.value)}
                </text>
              )}
              {items.length <= 12 && (
                <text className="viz-tick" x={x + bw / 2} y={H - 6} textAnchor="middle">
                  {it.label.length > 10 ? `${it.label.slice(0, 9)}…` : it.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
